import React from 'react';
import Button from '@atlaskit/button';
import Popup from '@atlaskit/popup'; // ignore-for-ENGHEALTH-17759
import { layers } from '@atlaskit/theme/constants';
import { useIntl } from '@atlassian/jira-intl';
import { WebhooksConfigurationLocation } from '@atlassian/jira-issue-get-design-updates-content/src/common/types.tsx';
import GetDesignUpdatesContent from '@atlassian/jira-issue-get-design-updates-content/src/ui/index.tsx';
import type { UIAnalyticsEvent } from '@atlassian/jira-product-analytics-bridge';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import messages from './messages.tsx';

type GetDesignUpdatesPopupProps = {
	isGetDesignUpdatesOpen: boolean;
	shouldRenderTriggerButton: boolean;
	setIsGetDesignUpdatesOpen: (value: boolean) => void;
	onOpenGetDesignUpdatesHandler: (
		_: React.SyntheticEvent,
		analyticsEvent: UIAnalyticsEvent,
	) => void;
};

export const GetDesignUpdatesPopup = ({
	isGetDesignUpdatesOpen,
	shouldRenderTriggerButton,
	setIsGetDesignUpdatesOpen,
	onOpenGetDesignUpdatesHandler,
}: GetDesignUpdatesPopupProps) => {
	const { isAdmin } = useTenantContext();
	const { formatMessage } = useIntl();

	return (
		<Popup
			isOpen={isGetDesignUpdatesOpen}
			onClose={() => setIsGetDesignUpdatesOpen(false)}
			placement="bottom-end"
			zIndex={layers.modal() + 1}
			content={() => (
				<GetDesignUpdatesContent
					onClose={() => setIsGetDesignUpdatesOpen(false)}
					location={WebhooksConfigurationLocation.SECTION}
				/>
			)}
			trigger={(triggerProps) =>
				shouldRenderTriggerButton ? (
					<Button
						{...triggerProps}
						appearance="subtle"
						isSelected={isGetDesignUpdatesOpen}
						testId="issue-design-section.common.ui.get-design-updates-popup.get-design-updates-button"
						onClick={onOpenGetDesignUpdatesHandler}
						interactionName="auto-generated.issue-design-section.ui.heading.get-design-updates-button.button"
					>
						{formatMessage(
							isAdmin ? messages.getDesignUpdatesButtonForAdmin : messages.getDesignUpdatesButton,
						)}
					</Button>
				) : (
					// Empty div is used here as we want to hide the button when shouldRenderTriggerButton is false,
					// but the Popup used for "Get design updates" still requires an anchor element to be attached to.
					<div aria-hidden="true" {...triggerProps} />
				)
			}
		/>
	);
};
